import { memo } from "react"
import { Link } from "@tanstack/react-router"

import { cn } from "@productlane/lib/src/browser"

import type { AgentItem } from "./agent-items"
import { codeFixTitle } from "./agent-items"
import { agentPath } from "./agent-route"
import { RecommendationTypeIcon } from "./recommendation-type-icon"

export function ideaTitle(item: AgentItem): string {
	switch (item.kind) {
		case "setup":
			return item.step.title
		case "recommendation":
			return item.recommendation.title
		case "code":
			return codeFixTitle(item.fix)
		case "docs":
			return item.group.title?.trim() || "Docs suggestion"
		case "proposal":
			return item.row.title.trim() || "Docs suggestion"
	}
}

// Long labels fade out at the edge instead of ending in an ellipsis, like Claude's chat list.
export const FADE_OUT_LABEL =
	"min-w-0 flex-1 overflow-hidden whitespace-nowrap [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]"

const DOT_DELAYS = ["0ms", "150ms", "300ms"]

export const TypingDots = memo(({ className }: { className?: string }) => (
	<span
		aria-hidden
		data-testid="agent-typing"
		className={cn("flex flex-none items-center gap-[3px]", className)}
	>
		{DOT_DELAYS.map((delay) => (
			<span
				key={delay}
				className="size-1 flex-none animate-pulse rounded-full bg-gray-400 dark:bg-gray-500"
				style={{ animationDelay: delay }}
			/>
		))}
	</span>
))

// Arrow keys can move the open idea past the list's edge, so its row follows.
function scrollIntoViewIfNeeded(node: HTMLElement | null) {
	node?.scrollIntoView?.({ block: "nearest" })
}

export const IdeaList = memo(
	({
		items,
		selectedId,
		writing,
		isUnread,
	}: {
		items: readonly AgentItem[]
		selectedId: string | null
		// The daily review is still reading conversations, so a new idea is on its way.
		writing: boolean
		isUnread: (id: string) => boolean
	}) => (
		<ul className="flex min-h-0 flex-1 list-none flex-col gap-px overflow-y-auto px-2 pb-3 pt-1">
			{writing && (
				<li
					data-testid="agent-idea-writing"
					className="flex h-9 items-center gap-3 rounded-md px-3 text-sm text-tertiary"
				>
					<span className="min-w-0 flex-1 truncate">New idea</span>
					<TypingDots />
				</li>
			)}
			{items.map((item) => {
				const isCursor = item.id === selectedId
				const unread = !isCursor && isUnread(item.id)
				return (
					<li key={item.id}>
						<Link
							ref={isCursor ? scrollIntoViewIfNeeded : undefined}
							to={agentPath("all", item.id)}
							aria-current={isCursor ? "true" : undefined}
							data-testid="agent-recommendation-row"
							data-kind={item.kind}
							data-unread={unread ? "true" : undefined}
							className={cn(
								"flex h-9 items-center gap-3 rounded-md px-3 text-sm text-primary",
								isCursor
									? "bg-gray-150 dark:bg-gray-850"
									: "hover:bg-gray-100 dark:hover:bg-gray-800",
							)}
						>
							<RecommendationTypeIcon
								type={item.type}
								articleIcon={
									"articleIcon" in item ? item.articleIcon : undefined
								}
							/>
							<span className={FADE_OUT_LABEL}>{ideaTitle(item)}</span>
							{/* The slot stays mounted so reading an idea never shifts the title. */}
							<span className="flex w-2 flex-none justify-end">
								<span
									className={cn(
										"size-1.5 rounded-full bg-blue-500",
										!unread && "opacity-0",
									)}
								/>
							</span>
						</Link>
					</li>
				)
			})}
		</ul>
	),
)
